import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAsk } from './Ask';
import { EMPTY_TABLE_STATE, stateFrom, useTableViews } from '../lib/tableViews';
import type { TableState } from '../lib/tableViews';
import { applyTableState, choicesFor, toCsv } from '../lib/tableData';
import { todayValue } from '../lib/dates';

/**
 * One column, described once.
 *
 * `render` draws the cell and `value` is the DATA BEHIND IT, and they are separate on
 * purpose: sorting, filtering and export all work on `value`, because you cannot sort a
 * `<span>` or put a badge in a CSV. A column with no `value` is decoration — an actions
 * column, a row of buttons — and is skipped by all three.
 */
export type Column<T> = {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  /**
   * Pins the column to the right edge, so it stays put while the rest scrolls under it.
   *
   * For a column of controls. A wide table overflows its frame — five columns of names, dates
   * and badges do not fit a laptop — and the column that falls off the end is the last one,
   * which is exactly where the buttons are. Sticky keeps them reachable at any width instead
   * of making people scroll sideways to find out what they can do.
   */
  stickyRight?: boolean;
  render: (row: T) => ReactNode;
  value?: (row: T) => string | number | null;
  /** What to order by, when that is not what the cell says — a priority's rank, say. */
  sortValue?: (row: T) => string | number | null;
  /**
   * `choice` offers the values actually present, which is nearly always what people want —
   * a status filter listing statuses nothing is in is a list of dead ends. `text` is for
   * anything free-form; `none` for a column not worth filtering.
   */
  filter?: 'text' | 'choice' | 'none';
  sortable?: boolean;
  /** Present but off until somebody turns it on in Columns. */
  hiddenByDefault?: boolean;
};


/** The classes a cell carries, shared by the header and the body so the two stay aligned. */
function cellClass<T>(column: Column<T>): string | undefined {
  const classes = [
    column.align === 'right' ? 'right' : column.align === 'center' ? 'center' : '',
    column.stickyRight ? 'grid-sticky-right' : '',
  ].filter(Boolean);
  return classes.length ? classes.join(' ') : undefined;
}

const PAGE_SIZES = [10, 25, 50, 100, 0];

/**
 * The list surface every table in the Hub uses.
 *
 * WHY ONE COMPONENT. Each list grew its own controls — some had a search box, some had chips,
 * one had an entries picker, most had no sorting at all — so the same question took a
 * different shape on every page. This is the grid behaviour asked for: click a header to
 * sort, filter per column, choose which columns show, page it, export what you are looking
 * at, and keep an arrangement you use often.
 *
 * FILTERING IS CLIENT-SIDE, over the rows the page already has. That is honest for the lists
 * here, which fetch what somebody may see and are hundreds of rows at most. A list that
 * cannot work that way — the activity log, which is unbounded — keeps its own server-side
 * search and hands this the page it got back.
 *
 * NOTHING OPENS IN A POPOVER. Panels expand in normal flow, because a menu inside a scroll
 * container gets clipped — the trap the notification panel fell into.
 */
export function DataTable<T extends { id: string }>({
  tableKey,
  columns,
  rows,
  empty,
  onRowClick,
  rowClassName,
  toolbar,
  defaultSort,
  searchPlaceholder,
  exportName,
  showSearch = true,
  showExport = true,
  showPaging = true,
  expand,
  pageState,
  onPageState,
}: {
  /** The list's own name for itself, and the key its saved views are filed under. */
  tableKey: string;
  columns: Column<T>[];
  rows: T[];
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  /** Anything the page needs beside the standard controls — a date range, a project picker. */
  toolbar?: ReactNode;
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
  searchPlaceholder?: string;
  /** Used for the download's filename. Defaults to the table key. */
  exportName?: string;
  /** Off where the page has its own search box, so there are never two. */
  showSearch?: boolean;
  /**
   * Off where the page exports SERVER-side.
   *
   * The activity log is the case: its export goes back to the API for up to 50,000 rows,
   * where this one can only write what the browser was sent. Two Export buttons meaning
   * different amounts of data is worse than one.
   */
  showExport?: boolean;
  /**
   * Off where the page pages on the SERVER.
   *
   * The activity log fetches a window and moves it with Newer/Older; a second pager inside
   * that window would be two controls that both say "page" and mean different things.
   */
  showPaging?: boolean;
  /**
   * The page's own filters, to be kept in a saved view alongside the table's.
   *
   * A page that has chips of its own — Issues has "mine", "overdue", a project picker — hands
   * them in here and takes them back through `onPageState` when a view is applied. Without
   * that, a view called "my open bugs" would restore the sort and lose the "mine".
   */
  /**
   * A panel that opens UNDER a row, for what does not fit in it — an audit record's stored
   * detail, say.
   *
   * Under the row it belongs to, never after the table: a panel rendered below fifty rows
   * opens far past the fold, and pressing the button looks like it did nothing.
   */
  expand?: { has: (row: T) => boolean; render: (row: T) => ReactNode; label?: string };
  pageState?: unknown;
  onPageState?: (value: unknown) => void;
}) {
  const ask = useAsk();
  const views = useTableViews(tableKey);

  const [state, setState] = useState<TableState>(() => ({
    ...EMPTY_TABLE_STATE,
    sort: defaultSort ?? null,
    hidden: columns.filter((column) => column.hiddenByDefault).map((column) => column.key),
  }));

  const [activeView, setActiveView] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  /** Which row has its panel open. One at a time: two open panels lose the row you were on. */
  const [opened, setOpened] = useState<string | null>(null);
  const [showColumns, setShowColumns] = useState(false);
  const [page, setPage] = useState(0);

  /**
   * Their default view, once, when it arrives.
   *
   * Guarded on `loaded` rather than on the array, or a table whose owner has no views would
   * keep trying, and guarded against re-applying so it cannot undo what somebody has since
   * changed on screen.
   */
  const [applied, setApplied] = useState(false);
  useEffect(() => {
    if (!views.loaded || applied) return;
    setApplied(true);

    const preferred = views.views.find((view) => view.isDefault);
    if (!preferred) return;

    const restored = stateFrom(preferred.state, EMPTY_TABLE_STATE);
    setState(restored);
    if (restored.page !== undefined) onPageState?.(restored.page);
    setActiveView(preferred.id);
  }, [views.loaded, views.views, applied]);

  const shownColumns = columns.filter((column) => !state.hidden.includes(column.key));

  /** The values present in each filterable column, so a choice list offers no dead ends. */
  const choices = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const column of columns) {
      if (column.filter === 'choice') map.set(column.key, choicesFor(rows, column));
    }
    return map;
  }, [columns, rows]);

  /**
   * Filtered and ordered in one place — `lib/tableData`, which knows nothing about React.
   *
   * There rather than here so the rules that decide what somebody sees can be tested on their
   * own: that a chosen value matches exactly and typed text matches a fragment, and that an
   * empty cell sinks whichever way the sort points.
   */
  const sorted = useMemo(
    () => applyTableState(rows, columns, state, shownColumns.map((column) => column.key)),
    [rows, columns, state, shownColumns],
  );

  /* A filter that shortens the list must not leave you looking at an empty page 4. */
  useEffect(() => {
    setPage(0);
  }, [state.filters, state.search, state.pageSize, rows.length]);

  /* A page that pages on the server hands us its window whole: never split it again. */
  const pageSize = (showPaging ? state.pageSize : 0) || sorted.length || 1;
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pages - 1);
  const paged = showPaging && state.pageSize;
  const visible = paged ? sorted.slice(current * pageSize, current * pageSize + pageSize) : sorted;

  const filterCount = Object.values(state.filters).filter(Boolean).length;
  const saved = views.views.find((view) => view.id === activeView)?.state;
  const dirty = activeView !== '' && !sameState({ ...state, page: pageState }, saved);

  function change(patch: Partial<TableState>) {
    setState((previous) => ({ ...previous, ...patch }));
  }

  function toggleSort(column: Column<T>) {
    if (column.sortable === false || !column.value) return;

    setState((previous) => {
      if (previous.sort?.key !== column.key) return { ...previous, sort: { key: column.key, direction: 'asc' } };
      /* Third press clears it, so there is a way back to the list's own order. */
      if (previous.sort.direction === 'asc') return { ...previous, sort: { key: column.key, direction: 'desc' } };
      return { ...previous, sort: null };
    });
  }

  function applyView(id: string) {
    setActiveView(id);
    if (!id) {
      change({ ...EMPTY_TABLE_STATE, sort: defaultSort ?? null, hidden: [] });
      return;
    }
    const view = views.views.find((entry) => entry.id === id);
    if (!view) return;

    const restored = stateFrom(view.state, EMPTY_TABLE_STATE);
    setState(restored);
    /* Only when the view actually carried some, so applying one never blanks a page's chips. */
    if (restored.page !== undefined) onPageState?.(restored.page);
  }

  /** The table's arrangement plus whatever the page keeps of its own. */
  function fullState(): TableState {
    return { ...state, page: pageState };
  }

  async function saveView() {
    const name = await ask.prompt({
      title: 'Save this view',
      body: 'The filters, sort, columns and page size as they are now. Only you will see it.',
      label: 'Call it',
      placeholder: 'My open bugs',
      confirmLabel: 'Save it',
      minLength: 1,
    });
    if (!name) return;

    const saved = await views.save(name.trim(), fullState());
    setActiveView(saved.id);
  }

  async function overwrite() {
    if (!activeView) return;
    await views.update(activeView, { state: fullState() });
  }

  async function removeView() {
    const view = views.views.find((entry) => entry.id === activeView);
    if (!view) return;

    const sure = await ask.confirm({
      title: `Delete “${view.name}”?`,
      body: 'Only the saved filter goes — nothing on the list itself.',
      confirmLabel: 'Delete it',
      danger: true,
    });
    if (!sure) return;

    await views.remove(view.id);
    applyView('');
  }

  function exportCsv() {
    /* What you are looking at, filtered and sorted — not the raw list behind it. */
    const body = toCsv(shownColumns, sorted);
    /* `todayValue`, not `toISOString`: a file exported at half past midnight was named yesterday. */
    download(`${exportName ?? tableKey}-${todayValue()}.csv`, body);
  }

  return (
    <div className="grid-table">
      <div className="grid-tools">
        {showSearch && (
          <input
            type="search"
            className="input-sm grid-search"
            value={state.search}
            placeholder={searchPlaceholder ?? 'Search this list'}
            onChange={(event) => change({ search: event.target.value })}
          />
        )}

        {toolbar}

        <button
          type="button"
          className={showFilters || filterCount ? 'chip-toggle is-on' : 'chip-toggle'}
          onClick={() => setShowFilters((open) => !open)}
        >
          Filters{filterCount ? ` (${filterCount})` : ''}
        </button>

        <button
          type="button"
          className={showColumns ? 'chip-toggle is-on' : 'chip-toggle'}
          onClick={() => setShowColumns((open) => !open)}
        >
          Columns{state.hidden.length ? ` (${shownColumns.length}/${columns.length})` : ''}
        </button>

        <span className="grow" />

        {/* Somebody's own saved arrangements. Never anybody else's — the API has no way to share one. */}
        <select
          className="input-sm"
          style={{ maxWidth: 190 }}
          value={activeView}
          onChange={(event) => applyView(event.target.value)}
        >
          <option value="">No saved view</option>
          {views.views.map((view) => (
            <option key={view.id} value={view.id}>
              {view.name}
              {view.isDefault ? ' ★' : ''}
            </option>
          ))}
        </select>

        {dirty && (
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => void overwrite()}>
            Update
          </button>
        )}
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => void saveView()}>
          Save view
        </button>

        {showPaging && (
          <select
            className="input-sm"
            style={{ maxWidth: 110 }}
            value={state.pageSize}
            onChange={(event) => change({ pageSize: Number(event.target.value) })}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size === 0 ? 'All rows' : `${size} rows`}
              </option>
            ))}
          </select>
        )}

        {showExport && (
          <button type="button" className="btn btn-quiet btn-sm" onClick={exportCsv} disabled={sorted.length === 0}>
            Export
          </button>
        )}
      </div>

      {activeView && (
        <div className="grid-view-bar">
          <span className="dim">
            Showing your saved view{dirty ? ' — changed since you saved it' : ''}
          </span>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={() => void views.update(activeView, { isDefault: true })}
          >
            Open this one by default
          </button>
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => void removeView()}>
            Delete view
          </button>
        </div>
      )}

      {showColumns && (
        <div className="grid-columns">
          {columns.map((column) => (
            <label key={column.key} className="row" style={{ gap: 6, fontSize: 12 }}>
              <input
                type="checkbox"
                checked={!state.hidden.includes(column.key)}
                onChange={(event) =>
                  change({
                    hidden: event.target.checked
                      ? state.hidden.filter((key) => key !== column.key)
                      : [...state.hidden, column.key],
                  })
                }
              />
              {column.header}
            </label>
          ))}
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => change({ hidden: [] })}>
            Show all
          </button>
        </div>
      )}

      <div className="grid-scroll">
        <table className="data grid-data">
          <thead>
            <tr>
              {shownColumns.map((column) => {
                const sortable = column.sortable !== false && Boolean(column.value);
                const sorting = state.sort?.key === column.key ? state.sort.direction : null;

                return (
                  <th
                    key={column.key}
                    style={{ width: column.width }}
                    className={cellClass(column)}
                    aria-sort={sorting === 'asc' ? 'ascending' : sorting === 'desc' ? 'descending' : 'none'}
                  >
                    {sortable ? (
                      <button type="button" className="grid-sort" onClick={() => toggleSort(column)}>
                        {column.header}
                        <span className={sorting ? 'grid-arrow is-on' : 'grid-arrow'}>
                          {sorting === 'desc' ? '▾' : '▴'}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
              {expand && <th style={{ width: '90px' }} />}
            </tr>

            {showFilters && (
              <tr className="grid-filters">
                {shownColumns.map((column) => (
                  <th key={column.key}>
                    {!column.value || column.filter === 'none' ? null : column.filter === 'choice' ? (
                      <select
                        className="input-sm"
                        value={state.filters[column.key] ?? ''}
                        onChange={(event) =>
                          change({ filters: { ...state.filters, [column.key]: event.target.value } })
                        }
                      >
                        <option value="">Any</option>
                        {(choices.get(column.key) ?? []).map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="input-sm"
                        value={state.filters[column.key] ?? ''}
                        placeholder="Contains…"
                        onChange={(event) =>
                          change({ filters: { ...state.filters, [column.key]: event.target.value } })
                        }
                      />
                    )}
                  </th>
                ))}
                {expand && <th />}
              </tr>
            )}
          </thead>

          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={shownColumns.length + (expand ? 1 : 0)} className="grid-empty">
                  {rows.length === 0 ? (empty ?? 'Nothing here yet.') : 'Nothing on this list matches those filters.'}
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <Fragment key={row.id}>
                  <tr
                    className={[rowClassName?.(row), onRowClick ? 'grid-clickable' : ''].filter(Boolean).join(' ')}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {shownColumns.map((column) => (
                      <td key={column.key} className={cellClass(column)}>
                        {column.render(row)}
                      </td>
                    ))}
                    {expand && (
                      <td className="right">
                        {expand.has(row) && (
                          <button
                            type="button"
                            className="btn btn-quiet btn-sm"
                            onClick={(event) => {
                              /* The row itself may open something else; this must not trigger it. */
                              event.stopPropagation();
                              setOpened(opened === row.id ? null : row.id);
                            }}
                          >
                            {opened === row.id ? 'Hide' : (expand.label ?? 'Detail')}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>

                  {expand && opened === row.id && (
                    <tr>
                      <td colSpan={shownColumns.length + 1} style={{ padding: 0 }}>
                        {expand.render(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="grid-foot">
        <span className="dim">
          {sorted.length === rows.length
            ? `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`
            : `${sorted.length} of ${rows.length} rows`}
          {paged && sorted.length > pageSize
            ? ` · showing ${current * pageSize + 1}–${current * pageSize + visible.length}`
            : ''}
        </span>

        {paged && pages > 1 && (
          <span className="row" style={{ gap: 6 }}>
            <button type="button" className="btn btn-quiet btn-sm" disabled={current === 0} onClick={() => setPage(current - 1)}>
              Back
            </button>
            <span className="dim" style={{ fontSize: 12 }}>
              Page {current + 1} of {pages}
            </span>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={current >= pages - 1}
              onClick={() => setPage(current + 1)}
            >
              Next
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

/** Two arrangements the same, for deciding whether a saved view has been changed on screen. */
function sameState(state: TableState, saved: Partial<TableState> | undefined): boolean {
  if (!saved) return false;
  return JSON.stringify(state) === JSON.stringify(stateFrom(saved, state));
}

function download(filename: string, body: string) {
  /* The BOM is what makes Excel read it as UTF-8 rather than mangling every accented name. */
  const blob = new Blob([`﻿${body}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = window.document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}
